import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { issuesApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircleIcon, MessageSquareIcon, XIcon, PhoneIcon } from './icons/LucideIcons';
import { ISSUE_LABELS } from './IssuesList';
import TextReactionLine from './TextReactionLine';
import { describeTextReaction } from '../utils/textReactions';

// A toast for every open problem report (and every customer text reply that
// isn't a plain thank-you), in every staff session. It stays
// until the report is resolved or this user closes it. Closing is per user
// and only hides the toast: the report stays on the Issues page until it is
// resolved. Checked every 60 seconds, like the dispatch map.

const POLL_MS = 60 * 1000;
const MAX_SHOWN = 3;

function dismissedKey(user) {
  return `rosa.dismissedIssues.${user?.id || user?.email || 'anon'}`;
}

function readDismissed(key) {
  try {
    return new Set(JSON.parse(localStorage.getItem(key) || '[]'));
  } catch {
    return new Set();
  }
}

function writeDismissed(key, ids) {
  try {
    localStorage.setItem(key, JSON.stringify([...ids]));
  } catch { /* storage unavailable: the toast just comes back next load */ }
}

const IssueToasts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const key = dismissedKey(user);
  const [issues, setIssues] = useState([]);
  const [dismissed, setDismissed] = useState(() => readDismissed(key));

  const load = useCallback(async () => {
    try {
      const res = await issuesApi.list({ status: 'open' });
      const open = res?.data || [];
      setIssues(open);
      window.dispatchEvent(new CustomEvent('rosa:open-issues', { detail: open.length }));
      // Forget dismissals for reports that are no longer open.
      setDismissed((prev) => {
        const openIds = new Set(open.map((i) => i.id));
        const next = new Set([...prev].filter((id) => openIds.has(id)));
        if (next.size !== prev.size) writeDismissed(key, next);
        return next;
      });
    } catch { /* offline or logged out: try again next tick */ }
  }, [key]);

  useEffect(() => {
    if (!user || user.accountType === 'driver') return undefined;
    load();
    const timer = setInterval(load, POLL_MS);
    window.addEventListener('rosa:issues-changed', load);
    return () => {
      clearInterval(timer);
      window.removeEventListener('rosa:issues-changed', load);
    };
  }, [user, load]);

  const dismiss = (id) => {
    setDismissed((prev) => {
      const next = new Set(prev).add(id);
      writeDismissed(key, next);
      return next;
    });
  };

  const visible = issues.filter((i) => !dismissed.has(i.id));
  if (visible.length === 0) return null;
  const shown = visible.slice(0, MAX_SHOWN);

  return (
    <div className="fixed bottom-20 right-4 z-50 flex w-[min(360px,calc(100vw-2rem))] flex-col gap-2 md:bottom-4" role="region" aria-label="Problem reports">
      {shown.map((issue) => (
        <div key={issue.id} className="rounded-md bg-gray-900 px-3.5 py-3 text-[13px] text-surface-alt shadow-lg">
          <div className="flex items-start gap-3">
            {issue.category === 'text_reply'
              ? <MessageSquareIcon size={16} className="mt-0.5 shrink-0 text-dot-red" />
              : <AlertCircleIcon size={16} className="mt-0.5 shrink-0 text-dot-red" />}
            <button
              type="button"
              onClick={() => navigate(`/orders/${encodeURIComponent(issue.order_number)}${issue.category === 'text_reply' ? '?tab=messages' : ''}`)}
              className="min-w-0 flex-1 text-left"
            >
              <div className="font-semibold">{ISSUE_LABELS[issue.category] || 'Problem reported'}</div>
              <div className="text-gray-300">
                Order {issue.shopify_order_name || issue.order_number}, from the {issue.audience}
                {issue.contact_name ? ` (${issue.contact_name})` : ''}
              </div>
              {issue.body && (describeTextReaction(issue.body)
                ? <TextReactionLine reaction={describeTextReaction(issue.body)} className="mt-1 text-gray-300 [&_.text-gray-600]:text-gray-400" />
                : <div className="mt-1 line-clamp-2 text-gray-300">{issue.body}</div>)}
            </button>
            <button type="button" onClick={() => dismiss(issue.id)} aria-label="Close this alert" className="shrink-0 text-gray-400 transition-colors duration-[120ms] hover:text-surface-alt">
              <XIcon size={16} />
            </button>
          </div>
          {issue.contact_phone && (
            <a href={`tel:${issue.contact_phone}`} className="ml-7 mt-2 inline-flex items-center gap-1 font-semibold text-floral-300">
              <PhoneIcon size={14} /> Call {issue.contact_phone}
            </a>
          )}
        </div>
      ))}
      {visible.length > MAX_SHOWN && (
        <button
          type="button"
          onClick={() => navigate('/issues')}
          className="self-end rounded-md bg-gray-900 px-3.5 py-2 text-[13px] font-semibold text-floral-300 shadow-lg transition-colors duration-[120ms] hover:bg-gray-800"
        >
          {visible.length - MAX_SHOWN} more on the Issues page
        </button>
      )}
    </div>
  );
};

export default IssueToasts;
